/**
 * Adaptadores PUROS entre el `data` por panel que devuelve el motor de Informes y lo que
 * esperan los widgets. Sin React; acá viven los tests de paridad.
 *
 * Contrato del motor (backend `ReportRenderService`):
 *  - kpi:   {metric, value, formatted_value, compare_value, formatted_compare_value, delta,
 *            formatted_delta, delta_direction, delta_sentiment, data_type, indicator_behavior}
 *  - bars/pie: {dimension, metric, metrics, rows:[{label, value, formatted_value, share, values, is_rest}], total}
 *  - line:  {metrics, time_grain, chartData, chartTimePeriods, dateRangeInfo}   (shape del Explorador)
 *  - table: {dimensions, metrics, tableData, summary, dateRangeInfo, truncated}  (shape del Explorador)
 */

import {rawPanelFilterNotes, rawPanelSummaryFilterNotes} from '../state/panelFilterNotes';

/**
 * Filas agrupadas → `chartData` de `SharedBarChart` (una serie por métrica).
 *
 * `excludedMetrics` son las que NO aplican con los filtros pedidos (`filter_notes`, tanda «Filtros
 * que se respetan»): una métrica que viaja en `null` nunca entra al eje como 0 — se saca de las
 * series y el motivo va en el ⓘ del encabezado. Sin la lista, idéntico a siempre.
 *
 * `labelOf(id)` da el nombre visible de cada serie (el del catálogo): sin él la leyenda y el
 * tooltip decían el id técnico, «Valor_de_compra» (Hesta, E9). Sin la función, el id como antes.
 */
export function barsChartData(data, excludedMetrics = [], labelOf = null) {
    if (!data || !Array.isArray(data.rows) || !data.rows.length) return null;
    const allMetrics = data.metrics && data.metrics.length ? data.metrics : [data.metric];
    const metrics = allMetrics.filter((metric) => !excludedMetrics.includes(metric));
    if (!metrics.length) return null;
    return {
        labels: data.rows.map((r) => r.label),
        series: metrics.map((metric) => ({
            name: metric,
            label: labelOf ? labelOf(metric) : metric,
            period: 'current',
            data: data.rows.map((r) => (r.values && metric in r.values ? r.values[metric] : (metric === data.metric ? r.value : null))),
            formattedData: data.rows.map((r) => (metric === data.metric ? r.formatted_value : null)),
        })),
    };
}

/**
 * ¿La torta tiene alguna porción que dibujar? `SharedPieChart` descarta las filas en 0 o nulas, así
 * que con TODAS en cero dibujaba un área vacía, sin torta, sin leyenda y sin decir nada — como si el
 * bloque estuviera roto (QA `QA-VIS-infev-03`). Pasa seguido con una métrica de eventos sobre
 * cuentas sin eventos elegidos, y el mismo criterio vale para cualquier métrica en cero.
 */
export function pieHasSlices(data) {
    if (!data || !Array.isArray(data.rows)) return false;
    return data.rows.some((r) => r && typeof r.value === 'number' && r.value > 0);
}

/**
 * El objeto `results` que consume `DataExplorerTable` (la tabla del Explorador, sin tocar). Las
 * notas de filtro viajan adentro (`filterNotes`) sólo cuando las hay: la tabla las resuelve por
 * columna con el mismo helper que el Explorador. Las de los totales (`summaryFilterNotes`, para el
 * Total) viajan sólo si el Backend mandó la clave, aunque sea vacía: su presencia es lo que dice
 * «los totales dicen otra cosa que la tabla» (`utils/filterNotes.summaryFilterNotesOf`).
 */
export function tableResults(data, filterNotes = null, summaryFilterNotes = undefined) {
    if (!data || !data.tableData) return null;
    const results = {
        tableData: data.tableData,
        summary: data.summary || {},
        dateRangeInfo: data.dateRangeInfo || null,
    };
    if (filterNotes) results.filterNotes = filterNotes;
    if (summaryFilterNotes !== undefined) results.summaryFilterNotes = summaryFilterNotes;
    return results;
}

/** ¿El panel de tabla pide ocultar las filas sin datos? (`panel.options.hide_empty_rows`, default no) */
export function hidesEmptyRows(panel) {
    return !!(panel && panel.options && panel.options.hide_empty_rows === true);
}

/**
 * ¿La tabla pinta el mapa de calor? (`panel.options.heatmap`). Prendido por defecto: lo que se guarda
 * es el `false` de quien lo apaga, y lo ve igual el cliente en el portal (tanda iVirtual, card xjcyp0F5).
 */
export function showsHeatmap(panel) {
    return !(panel && panel.options && panel.options.heatmap === false);
}

/** ¿La tabla agrupa por la primera columna? (`panel.options.group_by_first_column`, prendido por defecto) */
export function groupsByFirstColumn(panel) {
    return !(panel && panel.options && panel.options.group_by_first_column === false);
}

/**
 * La tabla de un panel YA RENDERIZADO (`result.panels[i]`) con sus notas: el Backend las deja en el
 * panel, al lado de `data`, no adentro (card x4TLNeOw). Es lo que exporta el PDF de Informes.
 */
export function panelTableResults(rendered) {
    if (!rendered) return null;
    return tableResults(rendered.data, rawPanelFilterNotes(rendered), rawPanelSummaryFilterNotes(rendered));
}

/**
 * El `chartData` + métricas seleccionadas para `DataExplorerLineChart`. `excludedMetrics`: las que
 * no aplican con los filtros (ver `barsChartData`); se sacan de las series y de la selección.
 */
export function lineChart(data, excludedMetrics = []) {
    if (!data || !data.chartData) return {chartData: null, selectedMetrics: []};
    if (!excludedMetrics.length) return {chartData: data.chartData, selectedMetrics: data.metrics || []};
    const series = (data.chartData.series || []).filter((s) => !excludedMetrics.includes(s.name));
    if (!series.length) return {chartData: null, selectedMetrics: []};
    return {
        chartData: {...data.chartData, series},
        selectedMetrics: (data.metrics || []).filter((m) => !excludedMetrics.includes(m)),
    };
}

// El Explorador formatea el delta con su propia flecha al final («+103.78% ↑»); la tarjeta pone
// la suya al principio según `delta_direction`. Sin limpiarla se veían las dos (QA caso 04).
const DELTA_ARROWS = /[↑↓↗↘▲▼]/g;
// El signo de adelante cuando ya hay flecha: «↑ +459.71%» dice dos veces lo mismo. El mockup
// muestra «↑ 12,4%». Sólo se saca si hay dirección: sin flecha, el signo es la única señal.
const LEADING_SIGN = /^[+\-−]\s*/;

/** La tarjeta KPI: qué mostrar y con qué tono. */
export function kpiView(data) {
    if (!data) return null;
    const direction = data.delta_direction || 'neutral';
    let delta = data.formatted_delta ? String(data.formatted_delta).replace(DELTA_ARROWS, '').trim() : null;
    if (delta && direction !== 'neutral') delta = delta.replace(LEADING_SIGN, '');
    return {
        value: data.formatted_value != null ? data.formatted_value : (data.value != null ? String(data.value) : '—'),
        delta: delta || null,
        direction,
        sentiment: data.delta_sentiment || 'neutral',
        compare: data.formatted_compare_value || null,
    };
}
